import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { MessageInputProps } from "./MessageInput.types";
import {
  StyledInput,
  StyledQuill,
  ToolbarSeparator,
} from "./MessageInput.styles";
import { Icon } from "../../../components/Icon/Icon";
import { colorPalette } from "../../../theme/colors";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";

export interface MessageInputRef {
  focus: () => void;
}

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  (
    {
      type,
      value,
      onChange,
      onSubmit,
      placeholder = "",
      maxLength,
      disabled = false,
      keyboardType = "default",
      dataCy = "message-input",
      showClearButton = true,
      onClear,
      onClick,
      showToolbar = true,
      showPlusButton = true,
      showPlusNotification = false,
      onPlusClick,
      allowEmptySubmit = false,
      isProcessing = false,
      onFormatClick,
    },
    ref
  ) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const textInputRef = useRef<HTMLTextAreaElement>(null);
    const [formats, setFormats] = useState<Record<string, any>>({});
    const previousHeightRef = useRef<number>(32);
    const isScrollingInputRef = useRef<boolean>(false);
    const touchStartYRef = useRef<number>(0);
    const touchStartScrollTopRef = useRef<number>(0);
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (type === "text" && textInputRef.current) {
            textInputRef.current.focus();
            // iOS-specific handling: click the input directly
            if (
              typeof window !== "undefined" &&
              /iPad|iPhone|iPod/.test(navigator.userAgent)
            ) {
              setTimeout(() => {
                if (textInputRef.current) {
                  textInputRef.current.focus();
                  textInputRef.current.click();
                }
              }, 0);
            }
          } else if (type === "rich" && quillRef.current) {
            const editor = quillRef.current.getEditor();
            editor.focus();
            // iOS-specific handling for rich text editor
            if (
              typeof window !== "undefined" &&
              /iPad|iPhone|iPod/.test(navigator.userAgent)
            ) {
              setTimeout(() => {
                const quillElement = editor.root;
                if (quillElement) {
                  quillElement.focus();
                  quillElement.click();
                }
              }, 0);
            }
          }
        },
      }),
      [type]
    );

    const handleTouchStart = useCallback(
      (e: React.TouchEvent) => {
        if (type === "text" && textInputRef.current) {
          touchStartYRef.current = e.touches[0].clientY;
          touchStartScrollTopRef.current = textInputRef.current.scrollTop;
          isScrollingInputRef.current = false;
        }
      },
      [type]
    );

    const handleTouchMove = useCallback(
      (e: React.TouchEvent) => {
        if (type === "text" && textInputRef.current) {
          const touchY = e.touches[0].clientY;
          const deltaY = touchY - touchStartYRef.current;
          const currentScrollTop = textInputRef.current.scrollTop;
          const scrollHeight = textInputRef.current.scrollHeight;
          const clientHeight = textInputRef.current.clientHeight;
          const isAtTop = currentScrollTop === 0;
          const isAtBottom =
            currentScrollTop + clientHeight >= scrollHeight - 1;

          // Check if we're trying to scroll within the textarea
          if (
            (deltaY > 0 && !isAtTop) || // Scrolling down and not at top
            (deltaY < 0 && !isAtBottom) // Scrolling up and not at bottom
          ) {
            isScrollingInputRef.current = true;
            e.stopPropagation(); // Prevent background scroll
          } else if (
            (deltaY > 0 && isAtTop) || // Trying to scroll down but at top
            (deltaY < 0 && isAtBottom) // Trying to scroll up but at bottom
          ) {
            // Allow background scroll only when at boundaries
            isScrollingInputRef.current = false;
          }
        }
      },
      [type]
    );

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
      if (isScrollingInputRef.current) {
        e.stopPropagation();
      }
      isScrollingInputRef.current = false;
    }, []);

    const dismissKeyboard = useCallback(() => {
      if (type === "rich" && quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.root.blur();
      }
    }, [type]);

    const handleClear = useCallback(() => {
      dismissKeyboard();
      if (onClear) {
        onClear();
      } else {
        if (type === "rich" && quillRef.current) {
          const editor = quillRef.current.getEditor();
          editor.setText("");
        }
        onChange("");
      }
    }, [onClear, onChange, dismissKeyboard, type]);

    const handlePlusClick = useCallback(() => {
      if (onPlusClick) {
        onPlusClick();
      }
    }, [onPlusClick]);

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let newValue = e.target.value.replace(/\r/g, "");

        // Validate input based on keyboardType
        if (keyboardType === "numeric") {
          const numericRegex = /^[0-9.-]*$/;
          if (!numericRegex.test(newValue)) {
            return;
          }
        }

        if (maxLength === undefined || newValue.length <= maxLength) {
          onChange(newValue);

          const textarea = e.target;

          // Check if content actually contains newlines
          const hasNewlines = newValue.includes("\n");

          if (newValue.trim() === "" || newValue === "") {
            // Completely empty - force single line
            textarea.style.height = "32px";
            previousHeightRef.current = 32;
          } else if (hasNewlines) {
            // Content has explicit newlines - use measurement element for accurate height
            const computedStyle = window.getComputedStyle(textarea);
            const measure = document.createElement("div");
            measure.style.position = "absolute";
            measure.style.visibility = "hidden";
            measure.style.height = "auto";
            measure.style.width = `${textarea.clientWidth}px`;
            measure.style.whiteSpace = "pre-wrap";
            measure.style.wordWrap = "break-word";
            measure.style.font = computedStyle.font;
            measure.style.fontSize = computedStyle.fontSize;
            measure.style.fontFamily = computedStyle.fontFamily;
            measure.style.fontWeight = computedStyle.fontWeight;
            measure.style.letterSpacing = computedStyle.letterSpacing;
            measure.style.padding = computedStyle.padding;
            measure.style.boxSizing = computedStyle.boxSizing;
            measure.style.border = computedStyle.border;
            measure.textContent = newValue;

            document.body.appendChild(measure);
            const measuredHeight = measure.offsetHeight;
            document.body.removeChild(measure);

            const finalHeight = Math.min(measuredHeight, 122);
            textarea.style.height = finalHeight + "px";
            previousHeightRef.current = finalHeight;
          } else {
            // No newlines - use measurement element to detect actual wrapping
            const computedStyle = window.getComputedStyle(textarea);
            const lineHeight = parseInt(computedStyle.lineHeight) || 17;

            // Create hidden measurement element
            const measure = document.createElement("div");
            measure.style.position = "absolute";
            measure.style.visibility = "hidden";
            measure.style.height = "auto";
            measure.style.width = `${textarea.clientWidth}px`;
            measure.style.whiteSpace = "pre-wrap";
            measure.style.wordWrap = "break-word";
            measure.style.font = computedStyle.font;
            measure.style.fontSize = computedStyle.fontSize;
            measure.style.fontFamily = computedStyle.fontFamily;
            measure.style.fontWeight = computedStyle.fontWeight;
            measure.style.letterSpacing = computedStyle.letterSpacing;
            measure.style.padding = computedStyle.padding;
            measure.style.boxSizing = computedStyle.boxSizing;
            measure.style.border = computedStyle.border;
            measure.textContent = newValue;

            document.body.appendChild(measure);
            const measuredHeight = measure.offsetHeight;
            document.body.removeChild(measure);

            // Check if measured height indicates wrapping (more than one line)
            const isWrapped = measuredHeight > lineHeight + 10; // Add buffer for measurement differences

            if (isWrapped) {
              // Content actually wrapped - use measured height (includes padding)
              const finalHeight = Math.min(measuredHeight, 122);
              textarea.style.height = finalHeight + "px";
              previousHeightRef.current = finalHeight;
            } else {
              // Single line - force 32px (don't use scrollHeight to avoid Safari bug)
              textarea.style.height = "32px";
              previousHeightRef.current = 32;
            }
          }
        }
      },
      [onChange, maxLength, keyboardType]
    );
    const handleQuillChange = useCallback(
      (content: string, delta: any, source: any, editor: any) => {
        try {
          const plainText = editor.getText().trim();

          if (maxLength !== undefined && plainText.length > maxLength) {
            return;
          }

          const isEmptyContent =
            !content ||
            content === "<p></p>" ||
            content === "<p><br></p>" ||
            content.trim() === "" ||
            plainText === "";

          if (isEmptyContent) {
            onChange("");
          } else {
            onChange(content);
          }
        } catch (error) {
          onChange(content);
        }
      },
      [onChange, maxLength]
    );

    const getCharacterCount = useCallback(() => {
      if (type === "text") {
        return value.length;
      }

      try {
        if (!quillRef.current) return 0;
        const editor = quillRef.current.getEditor();
        return Math.max(0, editor.getLength() - 1);
      } catch (error) {
        return 0;
      }
    }, [type, value]);

    const characterCount = getCharacterCount();
    const isOverLimit =
      typeof maxLength === "number" && characterCount > maxLength;
    const isSubmitDisabled =
      disabled ||
      (!allowEmptySubmit && (!value.trim() || characterCount === 0)) ||
      isOverLimit;

    const handleSubmit = useCallback(() => {
      // Update this condition to allow empty submit when allowEmptySubmit is true
      if (
        !disabled &&
        (allowEmptySubmit || value.trim()) &&
        !isOverLimit &&
        !isProcessing
      ) {
        dismissKeyboard();
        onSubmit(value);
      }
    }, [
      disabled,
      value,
      onSubmit,
      dismissKeyboard,
      isOverLimit,
      allowEmptySubmit,
      isProcessing,
    ]);

    const handleFormat = useCallback(
      (format: string) => {
        try {
          if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const isActive = !!formats[format];
            editor.format(format, !isActive);
            setTimeout(() => {
              setFormats(editor.getFormat());
            }, 10);
            if (onFormatClick) {
              onFormatClick(format);
            }
          }
        } catch (error) {
          console.error("Error handling format:", error);
        }
      },
      [formats]
    );

    const handleList = useCallback(
      (listType: string) => {
        try {
          if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const currentList = formats.list;

            if (listType === "checked") {
              if (currentList === "checked") {
                editor.format("list", "unchecked");
              } else if (currentList === "unchecked") {
                editor.format("list", false);
              } else {
                editor.format("list", "checked");
              }
            } else {
              const shouldRemoveList = currentList === listType;
              editor.format("list", shouldRemoveList ? false : listType);
            }

            setTimeout(() => {
              setFormats(editor.getFormat());
            }, 10);
            if (onFormatClick) {
              // Map list types to format names for GA
              const formatName =
                listType === "checked"
                  ? "checked"
                  : listType === "ordered"
                  ? "ordered"
                  : "bullet";
              onFormatClick(formatName);
            }
          }
        } catch (error) {
          console.error("Error handling list:", error);
        }
      },
      [formats]
    );

    const isFormatActive = useCallback(
      (format: string) => {
        return !!formats[format];
      },
      [formats]
    );

    const isListActive = useCallback(
      (listType: string) => {
        return formats.list === listType;
      },
      [formats]
    );

    const hasContent = value.trim().length > 0;

    // Subscribe to format changes for rich type
    useEffect(() => {
      if (type === "rich") {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const updateFormats = () => {
          try {
            setFormats(editor.getFormat());
          } catch (error) {
            // Silent error handling
          }
        };

        editor.on("selection-change", updateFormats);
        editor.on("text-change", updateFormats);
        updateFormats();

        return () => {
          try {
            editor.off("selection-change", updateFormats);
            editor.off("text-change", updateFormats);
          } catch (error) {
            console.error("Error unsubscribing from formats:", error);
          }
        };
      }
    }, [type]);

    const getInputProps = () => {
      const baseProps = {
        placeholder,
        disabled,
        onClick,
        minRows: 1,
        maxRows: 4,
      };

      switch (keyboardType) {
        case "numeric":
          return {
            ...baseProps,
            inputMode: "numeric" as const,
            pattern: "[0-9]*",
          };

        default:
          return {
            ...baseProps,
            inputMode: "text" as const,
            type: "text" as const,
          };
      }
    };

    const renderLeftButton = () => {
      if (showClearButton || hasContent) {
        return (
          <FlexColumn
            alignItems="center"
            justifyContent="center"
            onClick={handleClear}
            data-cy="clear-button"
            height="32px"
            width="32px"
            p={6}
          >
            <Icon
              name="fa-sharp fa-solid fa-circle-xmark"
              size="16"
              height="20"
              width="20"
              color={colorPalette.popBlack[200]}
            />
          </FlexColumn>
        );
      }

      if (showPlusButton) {
        return (
          <FlexColumn
            alignItems="center"
            justifyContent="center"
            onClick={handlePlusClick}
            data-cy="plus-button"
            height="32px"
            width="32px"
            p={6}
            className="collab-task-list-icon"
          >
            <Icon
              name="fa-solid fa-list-check"
              size="16"
              height="20"
              width="20"
              color={colorPalette.popBlack[200]}
              state={showPlusNotification ? "notification" : "default"}
              notificationColor={colorPalette.popOrange[500]}
            />
          </FlexColumn>
        );
      }

      return null;
    };

    const renderInput = () => {
      if (type === "text") {
        return (
          <FlexRow gap={8} alignItems="flex-end" px={10} pt={8} pb={16}>
            {renderLeftButton()}

            <StyledInput
              ref={textInputRef}
              value={value}
              onChange={handleTextChange}
              keyboardType={keyboardType}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              {...getInputProps()}
            />

            <FlexColumn
              alignItems="center"
              justifyContent="center"
              onClick={handleSubmit}
              height="32px"
              width="32px"
              px={10}
              flexShrink={0}
              borderRadius="50%"
              cursor={isSubmitDisabled || isProcessing ? "default" : "pointer"}
              backgroundColor={
                isSubmitDisabled || isProcessing
                  ? colorPalette.popBlack[100]
                  : colorPalette.success[500]
              }
              data-cy="submit-button"
            >
              {isProcessing ? (
                <Icon
                  name="fa-solid fa-loader fa-spin"
                  height="10"
                  width="12"
                  color={colorPalette.popWhite[500]}
                  size="10"
                />
              ) : (
                <Icon
                  name="fa-kit fa-small-arrow fa-rotate-180"
                  height="8"
                  width="12"
                  color={colorPalette.popWhite[500]}
                  size="8"
                />
              )}
            </FlexColumn>
          </FlexRow>
        );
      }

      // Rich text editor
      return (
        <FlexColumn>
          <FlexRow gap={8} alignItems="flex-end" px={10} pt={8} pb={16}>
            {renderLeftButton()}

            <StyledQuill
              onClick={onClick}
              onTouchStart={(e) => {
                const quillEditor = quillRef.current?.getEditor()?.root;
                if (quillEditor) {
                  const touchY = e.touches[0].clientY;
                  touchStartYRef.current = touchY;
                  touchStartScrollTopRef.current = quillEditor.scrollTop;
                  isScrollingInputRef.current = false;
                }
              }}
              onTouchMove={(e) => {
                const quillEditor = quillRef.current?.getEditor()?.root;
                if (quillEditor) {
                  const touchY = e.touches[0].clientY;
                  const deltaY = touchY - touchStartYRef.current;
                  const currentScrollTop = quillEditor.scrollTop;
                  const scrollHeight = quillEditor.scrollHeight;
                  const clientHeight = quillEditor.clientHeight;
                  const isAtTop = currentScrollTop === 0;
                  const isAtBottom =
                    currentScrollTop + clientHeight >= scrollHeight - 1;

                  if ((deltaY > 0 && !isAtTop) || (deltaY < 0 && !isAtBottom)) {
                    isScrollingInputRef.current = true;
                    e.stopPropagation();
                  }
                }
              }}
              onTouchEnd={(e) => {
                if (isScrollingInputRef.current) {
                  e.stopPropagation();
                }
                isScrollingInputRef.current = false;
              }}
            >
              <ReactQuill
                ref={quillRef}
                value={value}
                onChange={handleQuillChange}
                placeholder={placeholder}
                readOnly={disabled}
                modules={{
                  toolbar: false,
                  clipboard: {
                    matchVisual: false,
                  },
                }}
                formats={[
                  "bold",
                  "italic",
                  "underline",
                  "strike",
                  "list",
                  "bullet",
                  "indent",
                  "link",
                  "align",
                  "direction",
                  "code",
                  "code-block",
                ]}
                style={{ display: "block", width: "100%" }}
              />
            </StyledQuill>

            <FlexColumn
              alignItems="center"
              justifyContent="center"
              onClick={handleSubmit}
              height="32px"
              width="32px"
              px={10}
              flexShrink={0}
              borderRadius="50%"
              cursor={isSubmitDisabled ? "default" : "pointer"}
              backgroundColor={
                isSubmitDisabled || isProcessing
                  ? colorPalette.popBlack[100]
                  : colorPalette.success[500]
              }
              data-cy="submit-button"
            >
              {isProcessing ? (
                <Icon
                  name="fa-solid fa-loader fa-spin"
                  height="10"
                  width="12"
                  color={colorPalette.popWhite[500]}
                  size="10"
                />
              ) : (
                <Icon
                  name="fa-kit fa-small-arrow fa-rotate-180"
                  height="8"
                  width="12"
                  color={colorPalette.popWhite[500]}
                  size="8"
                />
              )}
            </FlexColumn>
          </FlexRow>

          {showToolbar && (
            <FlexRow px={10} py={7} gap={8} backgroundColor="#D3D6DC">
              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("bold")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("bold")}
              >
                <Icon
                  name="fa-solid fa-bold"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("bold")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                  cursor="pointer"
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("italic")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("italic")}
              >
                <Icon
                  name="fa-solid fa-italic"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("italic")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                  cursor="pointer"
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("underline")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("underline")}
              >
                <Icon
                  name="fa-solid fa-underline"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("underline")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("strike")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("strike")}
              >
                <Icon
                  name="fa-solid fa-strikethrough"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("strike")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <ToolbarSeparator>|</ToolbarSeparator>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isListActive("bullet")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleList("bullet")}
              >
                <Icon
                  name="fa-solid fa-list-ul"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isListActive("bullet")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isListActive("ordered")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleList("ordered")}
              >
                <Icon
                  name="fa-solid fa-list-ol"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isListActive("ordered")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isListActive("checked") || isListActive("unchecked")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleList("checked")}
              >
                <Icon
                  name="fa-solid fa-tasks"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isListActive("checked") || isListActive("unchecked")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>
            </FlexRow>
          )}
        </FlexColumn>
      );
    };

    return (
      <FlexColumn
        width="100%"
        data-cy={dataCy}
        gap={8}
        backgroundColor={colorPalette.popWhite[300]}
      >
        {renderInput()}
      </FlexColumn>
    );
  }
);
